import React, { useState } from 'react';
import { stockSpreadText, warningText, warningLevel } from '../POCreation/spreadView';
import { spreadLineText, boxReady } from '../../utils/spreadImport';

const numInput = 'w-20 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-1 text-sm text-right';
const cellCls = 'px-3 py-2 align-top text-sm text-gray-800 dark:text-gray-200';

/**
 * Lines of a V2 plan. Extension points for Task 14:
 *   renderActions(line, index)  -> node for the Actions cell
 *   renderOptions(line, index)  -> node for an expandable options panel (a toggle shows only when provided)
 *   onSetBox(line)              -> opens the box editor
 */
export default function LinesTable({ lines, onChange, onSetBox, renderActions, renderOptions }) {
  const [open, setOpen] = useState({});
  const remOf = (l) => (Number.isFinite(l.remaining) ? l.remaining : 0);
  const clamp = (v, max) => Math.max(0, Math.min(max, parseInt(v, 10) || 0));

  const setDepth = (i, v) => {
    const l = lines[i];
    const rem = remOf(l);
    const depthQty = clamp(v, rem);
    onChange(i, { depthQty, spreadQty: Math.min(l.spreadQty, rem - depthQty) });
  };
  const setSpread = (i, v) => {
    const l = lines[i];
    onChange(i, { spreadQty: clamp(v, remOf(l) - l.depthQty) });
  };

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            {['Product', 'Ordered / remaining', 'Distribution', 'Depth units', 'Spread units', 'Box', 'Actions'].map((h) => (
              <th key={h} className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">
          {lines.map((l, i) => {
            const level = warningLevel(l.spreadLive);
            const warn = warningText(l.spreadLive);
            const dist = stockSpreadText(l.spreadLive);
            const options = renderOptions ? renderOptions(l, i) : null;
            return (
              <React.Fragment key={`${l.asin}-${i}`}>
                <tr data-testid={`line-${l.asin}`}>
                  <td className={cellCls}>
                    <div className="font-mono text-xs">{l.asin}</div>
                    <div className="text-gray-600 dark:text-gray-400">{l.description}</div>
                  </td>
                  <td className={cellCls}>{`${l.ordered ?? '—'} / ${Number.isFinite(l.remaining) ? l.remaining : '—'}`}</td>
                  <td className={cellCls}>
                    <div>{dist || <span className="text-gray-400">Spread unknown</span>}</div>
                    {warn && (
                      <div className={level === 'LOW' ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}>
                        {warn}
                      </div>
                    )}
                  </td>
                  <td className={cellCls}>
                    <input aria-label={`Depth units ${l.asin}`} type="number" min="0" max={remOf(l)} className={numInput}
                      value={l.depthQty} onChange={(e) => setDepth(i, e.target.value)} />
                  </td>
                  <td className={cellCls}>
                    <input aria-label={`Spread units ${l.asin}`} type="number" min="0" max={Math.max(0, remOf(l) - l.depthQty)} className={numInput}
                      title={spreadLineText(l) || ''} value={l.spreadQty} onChange={(e) => setSpread(i, e.target.value)} />
                  </td>
                  <td className={cellCls}>
                    {boxReady(l.pkg)
                      ? <button type="button" aria-label={`Box size ${l.asin}`} className="text-green-700 dark:text-green-400" onClick={() => onSetBox && onSetBox(l)}>Box ready</button>
                      : <button type="button" aria-label={`Box size ${l.asin}`} className="text-blue-600 dark:text-blue-400 underline" onClick={() => onSetBox && onSetBox(l)}>Set box size</button>}
                  </td>
                  <td className={cellCls}>
                    {renderActions ? renderActions(l, i) : null}
                    {renderOptions && (
                      <button type="button" className="ml-2 text-xs text-gray-500 dark:text-gray-400 underline"
                        onClick={() => setOpen((o) => ({ ...o, [i]: !o[i] }))}>
                        {open[i] ? 'Hide options' : 'Options'}
                      </button>
                    )}
                  </td>
                </tr>
                {options && open[i] && (
                  <tr><td colSpan={7} className="px-3 py-2 bg-gray-50 dark:bg-gray-800">{options}</td></tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
